import * as THREE from 'three';

const clamp=THREE.MathUtils.clamp;

export function botObstacleClearance(position,yaw,world,maxDistance=5,margin=.66){
  const direction={x:-Math.sin(yaw),z:-Math.cos(yaw)};
  let nearest=maxDistance;
  for(const obstacle of world.obstacles){
    if(obstacle.minY>.85||obstacle.maxY<.04)continue;
    if(obstacle.shape==='circle'){
      const dx=position.x-obstacle.x;
      const dz=position.z-obstacle.z;
      const radius=obstacle.radius+margin;
      const projection=dx*direction.x+dz*direction.z;
      const discriminant=projection*projection-(dx*dx+dz*dz-radius*radius);
      if(discriminant<0)continue;
      const near=-projection-Math.sqrt(discriminant);
      const far=-projection+Math.sqrt(discriminant);
      const distance=near>=0?near:far>=0?0:null;
      if(distance!==null)nearest=Math.min(nearest,distance);
      continue;
    }
    let near=0;
    let far=maxDistance;
    for(const axis of ['x','z']){
      const origin=position[axis];
      const component=direction[axis];
      const minimum=obstacle[`min${axis.toUpperCase()}`]-margin;
      const maximum=obstacle[`max${axis.toUpperCase()}`]+margin;
      if(Math.abs(component)<.00001){
        if(origin<minimum||origin>maximum){near=Infinity;break;}
        continue;
      }
      let first=(minimum-origin)/component;
      let second=(maximum-origin)/component;
      if(first>second)[first,second]=[second,first];
      near=Math.max(near,first);
      far=Math.min(far,second);
      if(near>far){near=Infinity;break;}
    }
    if(Number.isFinite(near)&&near<=maxDistance)nearest=Math.min(nearest,Math.max(0,near));
  }
  return nearest;
}

// Ajusta el vector de navegación con la posición futura inmediata de los
// demás autos. Esto evita que varios bots elijan el mismo hueco y reaccionen
// recién cuando ya están superpuestos.
export function applyBotTrafficAvoidance(desired,vehicle,vehicles,options={}){
  const position=vehicle.car.position;
  const target=options.target||null;
  const prediction=options.prediction??.18;
  const radius=options.radius??3.35;
  const radiusSq=radius*radius;
  const strength=options.strength??.78;
  const forward=new THREE.Vector3(-Math.sin(vehicle.yaw),0,-Math.cos(vehicle.yaw));
  let traffic=0;
  for(const other of vehicles){
    if(other===vehicle||other.disabled||other===target)continue;
    const predictedX=other.car.position.x+other.velocity.x*prediction;
    const predictedZ=other.car.position.z+other.velocity.z*prediction;
    const dx=position.x-predictedX;
    const dz=position.z-predictedZ;
    const distanceSq=dx*dx+dz*dz;
    if(distanceSq<=.04||distanceSq>=radiusSq)continue;
    const distance=Math.sqrt(distanceSq);
    const force=(radius-distance)/Math.max(.45,distance)*strength;
    desired.x+=dx*force;
    desired.z+=dz*force;
    const towardX=-dx/distance;
    const towardZ=-dz/distance;
    if(forward.x*towardX+forward.z*towardZ>.48){
      traffic=Math.max(traffic,1-distance/radius);
    }
  }
  return {traffic};
}

export function botAirControls(vehicle,steer,boost=false){
  if(vehicle.wheelsGrounded)return {
    airPitch:boost?-.08:0,
    airYaw:steer*.78,
    airRoll:steer*.28,
  };
  const recovery=vehicle.airRecoveryError?.()||{
    pitch:-vehicle.pitch,
    roll:vehicle.roll,
  };
  return {
    // Al volar priorizamos volver a una actitud estable para aterrizar con las
    // cuatro ruedas, sin quitar por completo el control lateral del bot.
    airPitch:clamp(recovery.pitch*1.55-(boost?.06:0),-.9,.9),
    airYaw:clamp(steer*.92,-1,1),
    airRoll:clamp(recovery.roll*1.45+steer*.22,-1,1),
  };
}

export class BotTelemetry {
  constructor(){
    this.distance=0;
    this.stuckSeconds=0;
    this.boostSeconds=0;
    this.recoveries=0;
    this.rescues=0;
    this.targetChanges=0;
    this.lastTarget=null;
  }

  update(dt,vehicle,{stuck=false,target=null}={}){
    this.distance+=Math.abs(vehicle.forwardSpeed||0)*dt;
    if(stuck)this.stuckSeconds+=dt;
    if(vehicle.boosting)this.boostSeconds+=dt;
    if(target!==this.lastTarget){
      if(this.lastTarget!==null)this.targetChanges+=1;
      this.lastTarget=target;
    }
  }

  recordRecovery(rescue=false){
    this.recoveries+=1;
    if(rescue)this.rescues+=1;
  }

  snapshot(){return {
    distance:this.distance,
    stuckSeconds:this.stuckSeconds,
    boostSeconds:this.boostSeconds,
    recoveries:this.recoveries,
    rescues:this.rescues,
    targetChanges:this.targetChanges,
  };}
}
